import { RocketOutlined } from '@ant-design/icons'
import { Button, Skeleton, Space } from 'antd'
import Link from 'next/link'
import { NextRouter } from 'next/router'

import { ProjectLink } from '@/components/projects/ProjectLink'
import { getMenuItem, MenuItem } from '@/components/ui/common/MenuItem'
import messages, { IntlFunction } from '@/utils/messages'
import { getFeatureAvailabilityForWorkspace } from '@/utils/projects'

import { Project, Workspace, WorkspaceDescriptor } from '@/types/index.d'

import styles from './SiderMenu.module.css'

export const DEFAULT_PAGE_SIZE = 25

type SiderMenuItemProps = {
  children?: MenuItem[]
  feature: keyof WorkspaceDescriptor
  href: string | undefined
  icon?: JSX.Element
  isNewFeature?: boolean
  key: string
  router: NextRouter
  showPaywallModal: (id: string) => void | null // if null do nothing about paywall
  t: IntlFunction
  workspace: Workspace
} & MenuItem

// This component can accept
// 1) Sub Menu Item
// 2) Menu Item that does have sub menu items
// 3) Menu Item that doesn't have sub menu items
export function SiderMenuItem({
  children,
  feature,
  href,
  icon,
  key,
  router,
  showPaywallModal,
  t,
  workspace,
  ...rest
}: SiderMenuItemProps): MenuItem & { 'data-cy': string } {
  const { isFeatureAvailable, isFeatureBehindPaywall } =
    getFeatureAvailabilityForWorkspace(feature, workspace)
  const isFreemium = workspace.status === 'FREEMIUM'
  const text = <Space>{t(`SiderMenu.${key}` as keyof typeof messages)}</Space>

  const handleOnClick =
    isFeatureBehindPaywall && showPaywallModal
      ? () => showPaywallModal(feature)
      : () => (typeof href === 'undefined' ? null : router.push(href))

  const label =
    isFeatureBehindPaywall && showPaywallModal ? (
      <div className={styles.analytics}>
        <div className={styles.freemiumUpgrade}>
          <span>{t(`SiderMenu.${key}` as keyof typeof messages)}</span>
          {isFreemium && (
            <span
              className={styles.proFeature}
              data-cy={`SiderMenu.Freemium.tag`}
            >
              <RocketOutlined style={{ display: 'block' }} />
            </span>
          )}
        </div>
      </div>
    ) : // redundant so the user can copy the href of the menu item
    typeof href === 'undefined' ? (
      <span className="menu-item-title">{text}</span>
    ) : (
      <Link href={href}>
        <span className="menu-item-title">{text}</span>
      </Link>
    )

  if (isFeatureAvailable) {
    return {
      'data-cy': `SiderMenu.${key}`,
      ...{
        label,
        key,
        icon,
        onClick: handleOnClick,
        children: !isFreemium && children,
        ...rest,
      },
    }
  }
}

export function getProjectMenuItem(type: string, project: Project): any {
  return {
    'data-cy': `SiderMenu.repositories.${project.title}`,
    ...getMenuItem(
      <ProjectLink classNames="menu-item-title" project={project} />,
      `${type}-${project.id}`
    ),
  }
}

export function renderSkeletonMenuItems(prefix: string): any {
  return [0, 1, 2].map((index) =>
    getMenuItem(
      <Skeleton paragraph={false} active key={`skeleton-${prefix}-${index}`} />,
      `skeleton-${prefix}-${index}`
    )
  )
}

export function getLoadMoreButton(callbackFn, name, isLoading, t) {
  return (
    <Button
      type="default"
      style={{ width: '100%' }}
      onClick={callbackFn}
      loading={isLoading}
      disabled={isLoading}
    >
      {t(`SiderMenu.${name}.button.showmore`)}
    </Button>
  )
}
